import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { type PropsWithChildren, useEffect } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { DatePicker } from '#components/form/DatePicker.tsx';
import { ControlledFormInput } from '#components/form/FormInput.tsx';
import { ControlledMultiselect } from '#components/form/FormMultiselect.tsx';
import { ControlledSelect } from '#components/form/FormSelect.tsx';
import type { Order } from '#domain/Order.ts';
import type { Tour } from '#domain/Tour.ts';
import { useGetVehicleOptions } from '#hooks/resources/useGetVehicleOptions.ts';
import { RIODateTime } from '#lib/RIODateTime.ts';

import { getOrderOptions } from '../model/orderOptions.ts';
import type { TourDuplicateFormValues } from '../model/TourDuplicateFormState.ts';
import { TourDuplicateDialogItem } from './TourDuplicateDialogItem.tsx';
import { TourDuplicateNotificationsItem } from './TourDuplicateNotificationsItem.tsx';

type DuplicateOptionId =
    | 'keepTourName'
    | 'setDateToToday'
    | 'keepAllTourOrders'
    | 'keepSelectedVehicle'
    | 'duplicateDriverComments';

type TourDuplicateDialogProps = {
    formContext: UseFormReturn<TourDuplicateFormValues>;
    isOpen: boolean;
    selectedTour: Tour;
    onSubmit: (values: TourDuplicateFormValues) => Promise<void>;
    setShowStopOrderDialog: (show: boolean) => void;
    orders: Order[];
    isDuplicating: boolean;
    isLoadingOrders: boolean;
    isOrderError: boolean;
    handleDialogClose: () => void;
};

export const TourDuplicateDialog = ({
    formContext,
    isOpen,
    selectedTour,
    onSubmit,
    setShowStopOrderDialog,
    orders,
    isDuplicating,
    isLoadingOrders,
    isOrderError,
    handleDialogClose,
}: TourDuplicateDialogProps) => {
    const intl = useIntl();
    const dialogTitleId = 'intl-msg:ordercom-cloud-frontend.tours.duplicate.dialog.title';

    const vehicleOptions = useGetVehicleOptions({ skip: !isOpen });

    const { watch, setValue, handleSubmit } = formContext;

    const keepTourName = watch('keepTourName');
    const setDateToToday = watch('setDateToToday');
    const keepAllTourOrders = watch('keepAllTourOrders');
    const keepSelectedVehicle = watch('keepSelectedVehicle');
    const selectedOrderIds = watch('selectedOrderIds');
    const relatedOrderIds = orders.map(order => order.id);

    useEffect(() => {
        if (!keepAllTourOrders) {
            return;
        }

        const hasSameSelection = selectedOrderIds.length === relatedOrderIds.length;

        if (!hasSameSelection) {
            setValue('selectedOrderIds', relatedOrderIds, { shouldValidate: true });
        }
    }, [keepAllTourOrders, orders, selectedOrderIds, setValue]);

    const resetOptionValues = (optionId: DuplicateOptionId) => {
        switch (optionId) {
            case 'keepTourName':
                setValue('tourName', selectedTour.searchName, { shouldValidate: true });
                return;
            case 'setDateToToday':
                setValue('orderDate', RIODateTime.Now().toPredefinedFormat(RIODateTime.PREDEFINED_FORMATS.DAY_ISO), {
                    shouldValidate: true,
                });
                return;
            case 'keepAllTourOrders':
                setValue('selectedOrderIds', relatedOrderIds, { shouldValidate: true });
                setShowStopOrderDialog(false);
                return;
            case 'keepSelectedVehicle':
                setValue('vehicleId', selectedTour.truck.id, { shouldValidate: true });
                return;
        }
    };

    const dialogItem = ({
        optionId,
        intlKey,
        children,
    }: PropsWithChildren & { optionId: DuplicateOptionId; intlKey: string }) => (
        <TourDuplicateDialogItem
            key={optionId}
            label={intl.formatMessage({
                id: intlKey,
            })}
            checked={watch(optionId)}
            onChange={event => {
                const checked = event.target.checked;
                setValue(optionId, checked, { shouldValidate: true });
                if (checked) {
                    resetOptionValues(optionId);
                }
            }}
        >
            {children}
        </TourDuplicateDialogItem>
    );

    const dialogBody = (
        <FormProvider {...formContext}>
            <form
                id='tour-duplicate-form'
                onSubmit={handleSubmit(onSubmit)}
                className='display-flex flex-column gap-15'
            >
                <div>
                    {intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.dialog.description',
                    })}
                </div>
                <ul className='list-group margin-bottom-0 margin-right-10'>
                    {dialogItem({
                        optionId: 'keepTourName',
                        intlKey: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.option.keep-tour-name',
                        children: !keepTourName && (
                            <div className='margin-y-10 max-width-250'>
                                <ControlledFormInput
                                    control={formContext.control}
                                    name='tourName'
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.tour-name',
                                    })}
                                    placeholder={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.tour-name.placeholder',
                                    })}
                                    formGroupClassName='margin-bottom-0'
                                />
                            </div>
                        ),
                    })}
                    {dialogItem({
                        optionId: 'setDateToToday',
                        intlKey: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.option.set-date-to-today',
                        children: !setDateToToday ? (
                            <div className='margin-y-10 max-width-250'>
                                <DatePicker
                                    control={formContext.control}
                                    name='orderDate'
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.order-date',
                                    })}
                                    className='margin-bottom-0'
                                    allowPastDates={false}
                                    fieldValueMode='dayIso'
                                />
                            </div>
                        ) : undefined,
                    })}
                    {dialogItem({
                        optionId: 'keepAllTourOrders',
                        intlKey: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.option.keep-all-tour-orders',
                        children: !keepAllTourOrders ? (
                            <>
                                <div className='margin-y-10 max-width-250'>
                                    <ControlledMultiselect
                                        control={formContext.control}
                                        name='selectedOrderIds'
                                        label={intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.orders',
                                        })}
                                        placeholder={intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.orders.placeholder',
                                        })}
                                        options={getOrderOptions(orders, selectedOrderIds)}
                                        counterMessage={{
                                            one: intl.formatMessage({
                                                id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.orders.selected-count',
                                            }),
                                            many: intl.formatMessage({
                                                id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.orders.selected-count',
                                            }),
                                        }}
                                        useFilter
                                        formGroupClassName='margin-bottom-0'
                                    />
                                </div>
                                <div className='margin-y-10'>
                                    <Button
                                        bsStyle='default'
                                        iconName='sort'
                                        autoTrackingKey='intl-msg:ordercom-cloud-frontend.tours.duplicate.button.adjust-stop-order'
                                        disabled={selectedOrderIds.length === 0}
                                        onClick={() => setShowStopOrderDialog(true)}
                                    >
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.button.adjust-stop-order',
                                        })}
                                    </Button>
                                </div>
                            </>
                        ) : undefined,
                    })}
                    {dialogItem({
                        optionId: 'keepSelectedVehicle',
                        intlKey: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.option.keep-selected-vehicle',
                        children: !keepSelectedVehicle ? (
                            <div className='margin-y-10 max-width-250'>
                                <ControlledSelect
                                    control={formContext.control}
                                    name='vehicleId'
                                    label={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.vehicle',
                                    })}
                                    placeholder={intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.input.vehicle.placeholder',
                                    })}
                                    options={vehicleOptions}
                                    useFilter
                                    formGroupClassName='margin-bottom-0'
                                />
                            </div>
                        ) : undefined,
                    })}
                    {dialogItem({
                        optionId: 'duplicateDriverComments',
                        intlKey: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.option.duplicate-driver-comments',
                    })}
                    <TourDuplicateNotificationsItem
                        checked={watch('notificationsEnabled')}
                        onChange={checked => setValue('notificationsEnabled', checked, { shouldValidate: true })}
                    />
                </ul>
                {isLoadingOrders && (
                    <div className='text-color-info'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.state.orders-loading',
                        })}
                    </div>
                )}
                {isOrderError && (
                    <div className='text-color-danger'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.state.orders-error',
                        })}
                    </div>
                )}
                {!keepSelectedVehicle && vehicleOptions.length === 0 && (
                    <div className='text-color-danger'>
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.state.vehicles-empty',
                        })}
                    </div>
                )}
            </form>
        </FormProvider>
    );

    return (
        <Dialog
            show={isOpen}
            title={intl.formatMessage({
                id: dialogTitleId,
            })}
            body={dialogBody}
            footer={
                <ButtonToolbar alignment='space-between' className='width-100pct'>
                    <Button
                        bsStyle='default'
                        autoTrackingKey='intl-msg:ordercom-cloud-frontend.common.cancel'
                        disabled={isDuplicating}
                        onClick={handleDialogClose}
                    >
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.common.cancel',
                        })}
                    </Button>
                    <Button
                        bsStyle='primary'
                        iconName='duplicate'
                        form='tour-duplicate-form'
                        type='submit'
                        autoTrackingKey='intl-msg:ordercom-cloud-frontend.tours.duplicate.button.submit'
                        disabled={isDuplicating}
                    >
                        {intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.tours.duplicate.button.submit',
                        })}
                    </Button>
                </ButtonToolbar>
            }
            onClose={handleDialogClose}
            bsSize='sm'
            showCloseButton={false}
            headerButtons={
                <Button
                    bsStyle={Button.MUTED}
                    iconOnly
                    iconName='remove'
                    autoTrackingKey={`${dialogTitleId}.close`}
                    className='modal-header-close close'
                    onClick={handleDialogClose}
                    aria-label='dialog close button'
                />
            }
            data-testid='tour-duplicate-dialog'
        />
    );
};
